import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, Loader2, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, money, shareUrl } from "../lib/api";
import { TicketDialog } from "../components/site/TicketDialog";
import { AddToCalendar } from "../components/site/AddToCalendar";

const nice = (d) => new Date(d + "T00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export const ShareButton = ({ concert, testid = "show-share-button", className = "" }) => {
  const share = async () => {
    const url = shareUrl(concert.id);
    const text = `00shot — ${concert.title} · ${concert.city}`;
    if (navigator.share) {
      try {
        return await navigator.share({ title: text, url });
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Show link copied — paste it anywhere to share");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };
  return (
    <button onClick={share} data-testid={testid} className={`btn-line rounded-full px-6 py-3 font-cond uppercase font-bold tracking-widest inline-flex items-center gap-2 ${className}`}>
      <Share2 className="w-4 h-4" /> Share
    </button>
  );
};

export default function ShowPage() {
  const { id } = useParams();
  const [c, setC] = useState(undefined);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.concert(id).then(setC).catch(() => setC(null));
  }, [id]);

  if (c === undefined) return <div className="px-5 lg:px-12 pt-20"><Loader2 className="w-8 h-8 animate-spin text-lime" /></div>;
  if (c === null)
    return (
      <div className="px-5 lg:px-12 pt-20" data-testid="show-not-found">
        <h1 className="font-cond uppercase font-black text-5xl">Show not found</h1>
        <Link to="/shows" className="text-lime font-cond uppercase tracking-widest mt-6 inline-block">See all shows</Link>
      </div>
    );

  const past = c.date < new Date().toISOString().slice(0, 10);
  const left = Math.max(0, c.capacity - c.tickets_sold);

  return (
    <section className="px-5 lg:px-12 pt-10" data-testid="show-page">
      <Link to="/shows" data-testid="show-back-link" className="text-slate-400 hover:text-lime font-cond uppercase tracking-widest text-sm inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> All shows</Link>
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] mt-8 items-start">
        <div className="rounded-2xl overflow-hidden card-surface aspect-[16/10]">
          {c.image_url ? <img src={imgSrc(c.image_url)} alt={c.title} className="w-full h-full object-cover" /> : <img src={shareUrl(c.id) + ".png"} alt={c.title} className="w-full h-full object-cover" />}
        </div>
        <div className="reveal">
          <p className="font-cond uppercase tracking-[0.35em] text-sm text-lime">{c.city}</p>
          <h1 data-testid="show-title" className="font-gothic text-5xl sm:text-6xl leading-[0.95] mt-3">{c.title}</h1>
          <div className="mt-6 space-y-2 text-slate-300">
            <p className="flex items-center gap-2" data-testid="show-date"><CalendarDays className="w-4 h-4 text-lime" /> {nice(c.date)}</p>
            <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-lime" /> {c.time}</p>
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-lime" /> {c.venue}, {c.city}</p>
          </div>
          {c.description && <p className="text-slate-400 mt-6">{c.description}</p>}
          <p className="font-cond uppercase tracking-widest text-3xl text-lime mt-8">{money(c.price)}</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="show-tickets-left">{past ? "This show has passed" : left === 0 ? "Sold out" : `${left} tickets left`}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => setOpen(c)} disabled={past || left === 0} data-testid="show-page-tickets-button"
              className="btn-lime rounded-full px-8 py-3 font-cond uppercase font-bold tracking-widest">Get tickets</button>
            <ShareButton concert={c} />
            <AddToCalendar show={c} testid="show-add-to-calendar" />
          </div>
        </div>
      </div>
      <TicketDialog concert={open} onClose={() => setOpen(null)} />
    </section>
  );
}
